import { formatPesoAmount } from '../utils/money';
import React, { useEffect, useId, useRef, useState } from 'react';
import { deleteExpense, updateExpense } from '../api';
import VarietyEditorDialog from './varietyPricing/VarietyEditorDialog';

const ALLOCATIONS = { volume: 'Volumen', weight: 'Peso', price: 'Precio' };
const draftFor = (expense) => ({ title: expense.title || '', amount: String(expense.amount ?? ''), description: expense.description || '', allocationBasis: expense.allocationBasis || '' });
const money = (amount) => `$${formatPesoAmount(Number(amount || 0))}`;
const inputClass = 'mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100';
const buttonClass = 'rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50';

export default function StockExpenseDialog({ expense, branchId, stockListId, isImportList = false, canUpdate = false, canDelete = false, initialMode = 'view', onClose, onChanged }) {
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(() => draftFor(expense));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const mounted = useRef(true);
  const formId = `stock-expense-${useId().replace(/:/g, '')}`;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const editing = mode === 'edit' && canUpdate;
  const deleting = mode === 'delete' && canDelete;
  const changeMode = (next) => { setError(''); setForm(draftFor(expense)); setMode(next); };
  const change = (key, value) => { setForm((current) => ({ ...current, [key]: value })); setError(''); };
  const run = async (action, payload) => {
    if (busyRef.current || (action === 'updated' ? !canUpdate : !canDelete)) return;
    if (!branchId || !stockListId || String(expense.stockListId) !== String(stockListId)) {
      setError('Este gasto no corresponde a la entrada abierta. Vuelve a cargarla.');
      return;
    }
    busyRef.current = true; setBusy(true); setError('');
    try {
      const result = action === 'updated'
        ? await updateExpense(branchId, expense.id, payload)
        : await deleteExpense(branchId, expense.id);
      if (mounted.current) onChanged({ action, id: expense.id, expense: action === 'updated' ? { ...expense, ...result } : null });
    } catch (err) {
      if (mounted.current) setError(err?.response?.data?.message || 'No se pudo guardar el cambio. Puedes reintentar.');
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const save = (event) => {
    event.preventDefault();
    if (!canUpdate || busyRef.current) return;
    const amount = Number(form.amount);
    if (!form.title.trim()) { setError('Indica el título del gasto.'); return; }
    if (!String(form.amount).trim() || !Number.isSafeInteger(amount) || amount < 0 || amount > 2147483647) {
      setError('Indica un monto válido en pesos enteros, igual o mayor que cero.'); return;
    }
    if (isImportList && !ALLOCATIONS[form.allocationBasis]) { setError('Selecciona cómo prorratear el gasto.'); return; }
    const payload = {};
    if (amount !== Number(expense.amount)) payload.amount = amount;
    if (form.title.trim() !== (expense.title || '').trim()) payload.title = form.title.trim();
    if (form.description.trim() !== (expense.description || '').trim()) payload.description = form.description.trim();
    if (isImportList && form.allocationBasis !== expense.allocationBasis) payload.allocationBasis = form.allocationBasis;
    if (!Object.keys(payload).length) { changeMode('view'); return; }
    void run('updated', payload);
  };

  return <VarietyEditorDialog
    eyebrow="Gastos de la entrada"
    title={`${deleting ? 'Eliminar gasto' : editing ? 'Editar gasto' : 'Detalle de gasto'}: ${expense.title}`}
    onClose={onClose} busy={busy} closeLabel="Cerrar gasto"
    footerNote="El monto y el prorrateo forman parte del costo de la entrada."
    footerActions={editing ? <>
      <button type="button" disabled={busy} className={`${buttonClass} border border-slate-300`} onClick={() => changeMode('view')}>Cancelar edición</button>
      <button type="submit" form={formId} disabled={busy} className={`${buttonClass} bg-blue-600 text-white`}>{busy ? 'Guardando…' : 'Guardar cambios'}</button>
    </> : deleting ? <>
      <button type="button" disabled={busy} className={`${buttonClass} border border-slate-300`} onClick={() => changeMode('view')}>Cancelar</button>
      <button type="button" disabled={busy} className={`${buttonClass} bg-red-600 text-white`} onClick={() => run('deleted')}>{busy ? 'Eliminando…' : 'Confirmar eliminación'}</button>
    </> : <>
      {canUpdate && <button type="button" className={`${buttonClass} bg-blue-600 text-white`} onClick={() => changeMode('edit')}>Editar gasto</button>}
      {canDelete && <button type="button" className={`${buttonClass} border border-red-300 text-red-700`} onClick={() => changeMode('delete')}>Eliminar gasto</button>}
      <button type="button" className={`${buttonClass} border border-slate-300`} onClick={onClose}>Cerrar</button>
    </>}
  >
    <div className="mx-auto max-w-2xl space-y-4">
      {editing ? <form id={formId} onSubmit={save} noValidate className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-semibold">Título<input name="title" className={inputClass} value={form.title} disabled={busy} onChange={(event) => change('title', event.target.value)} /></label>
        <label className="text-sm font-semibold">Monto (CLP)<input name="amount" type="number" min="0" step="1" className={inputClass} value={form.amount} disabled={busy} onChange={(event) => change('amount', event.target.value)} /></label>
        <label className="text-sm font-semibold sm:col-span-2">Descripción<textarea name="description" className={inputClass} rows={3} value={form.description} disabled={busy} onChange={(event) => change('description', event.target.value)} /></label>
        {isImportList && <label className="text-sm font-semibold">Prorrateo<select name="allocationBasis" className={inputClass} value={form.allocationBasis} disabled={busy} onChange={(event) => change('allocationBasis', event.target.value)}>
          <option value="">Seleccionar</option>{Object.entries(ALLOCATIONS).map(([value, label]) => <option key={value} value={value}>Por {label.toLowerCase()}</option>)}
        </select></label>}
      </form> : <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-800">
        {deleting && <p className="font-semibold text-red-800">¿Eliminar este gasto de la entrada?</p>}
        <p className="text-lg font-bold">{money(expense.amount)}</p>
        {expense.description && <p>{expense.description}</p>}
        {isImportList && <p>Prorrateo: {ALLOCATIONS[expense.allocationBasis] || 'Pendiente'}</p>}
      </div>}
      {error && <p role="alert" className="text-sm font-semibold text-red-700">{error}</p>}
    </div>
  </VarietyEditorDialog>;
}
